// theme/presets/cardMaterial.ts — the card "material" tokens (sheen / grain / edge / elevation).
//
// A card is modelled as one physical surface: an opaque base fill (see cardSurface.ts)
// with up to two additive layers composited on top, plus a drop shadow. Every property
// collapses to nothing at 0, so the shipped default look is byte-identical to the
// pre-feature tokens.
//
// ⚠ THE CAPS BELOW ARE COUPLED TO THE AA LUMINANCE BAND IN cardSurface.ts.
// The dark band is only ~0.0125 wide and sheen + grain consume a real fraction of it.
// RAISING ANY CAP HERE INVALIDATES THE VERIFIED CONTRAST TABLE — re-run the band solve
// (see cardSurface.ts CARD_LUMA_BAND) before touching these numbers.

/** Ready-to-interpolate card material tokens. ONLY the card primitives read these. */
export interface MaterialTokens {
  sheen: number;
  grain: number;
  edge: number;
  elevation: number;
  /** `background-image` layer stack — 'none' when flat. */
  backgroundImage: string;
  /** `background-size` matching the layer stack — 'auto' when flat. */
  backgroundSize: string;
  backgroundRepeat: string;
  /** Complete box-shadow for a resting card. */
  shadow: string;
  /** Complete box-shadow for an `$elevated` card. */
  shadowElevated: string;
}

/** The scalar inputs resolveMaterial needs (UserPreferences satisfies this structurally). */
export interface MaterialInput {
  cardSheen: number;
  cardGrain: number;
  cardEdge: number;
  cardElevation: number;
}

export const MATERIAL_CAPS = {
  dark: {
    sheen: 0.035, sheenBandPx: 72, sheenRgb: '255, 255, 255', sheenSrc: [255, 255, 255],
    grain: 0.03, edge: 0.2, shadowRgb: '0, 0, 0',
    a0: 0.15, aSm: 0.25, aMd: 0.35,
    mdInset: '',
  },
  light: {
    sheen: 0.05, sheenBandPx: 88, sheenRgb: '20, 18, 22', sheenSrc: [20, 18, 22],
    grain: 0.03, edge: 0.6, shadowRgb: '20, 18, 22',
    a0: 0.04, aSm: 0.08, aMd: 0.14,
    // No inset bevel. A white inner highlight along a card's top edge is the
    // skeuomorphic 'glass pane' cue that shipped with every 2021 template.
    mdInset: '',
  },
} as const;

/** Grain is quantised to 5 named stops — a continuous noise slider reads as jitter. */
export const GRAIN_STEPS = [0, 0.25, 0.5, 0.75, 1] as const;
export const GRAIN_LABELS = ['None', 'Fine', 'Medium', 'Coarse', 'Max'] as const;

export const quantiseGrain = (n: number): number =>
  Math.round(Math.min(1, Math.max(0, n)) * 4) / 4;

/** Label for a (quantised) grain value. */
export function grainLabel(n: number): string {
  const idx = Math.round(quantiseGrain(n) * 4);
  return GRAIN_LABELS[idx] ?? 'None';
}

const TILE = 160;
// ≤ 10 entries for the app lifetime (5 stops × 2 modes) — the SVG is built once per stop.
const grainCache = new Map<string, string>();

function grainTile(step: number, mode: 'dark' | 'light'): string {
  const key = `${mode}:${step}`;
  const hit = grainCache.get(key);
  if (hit) return hit;
  const a = (MATERIAL_CAPS[mode].grain * step).toFixed(3);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE}' height='${TILE}'>` +
    `<filter id='n' x='0' y='0' width='100%' height='100%'>` +
    `<feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/>` +
    `<feColorMatrix type='saturate' values='0'/>` +
    `</filter>` +
    `<rect width='100%' height='100%' filter='url(#n)' opacity='${a}'/>` +
    `</svg>`;
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg).replace(/'/g, '%27')}")`;
  grainCache.set(key, url);
  return url;
}

// A zero length renders as bare `0`, not `0px`, so a flat/seated shadow is
// byte-identical to the elevation token it reproduces.
const px = (n: number): string => (n === 0 ? '0' : `${+n.toFixed(1)}px`);

/**
 * Piecewise-linear drop shadow through three anchors: none → elevation.sm →
 * elevation.md. At t = 0.5 this reproduces the shipped elevation.sm
 * byte-for-byte, which is what keeps the Card Appearance defaults identical to
 * the theme a member sees before they ever open Appearance.
 *
 * 1.25.00 retuned the anchors: a card at rest is a 1px ground line, not a 14px
 * blurred drop shadow. Black drop shadows go muddy on a near-black surface and
 * read as template depth; the slider still spans three visibly different stops
 * (nothing → a seated line → a genuinely floating card), so it stays useful.
 */
export function elevationFor(t: number, mode: 'dark' | 'light'): string {
  const c = MATERIAL_CAPS[mode];
  const clamped = Math.min(1, Math.max(0, t));
  const lo = clamped <= 0.5;
  const u = lo ? clamped * 2 : (clamped - 0.5) * 2;
  const y = lo ? u : 1 + 7 * u; //  0 →  1 →  8
  const b = lo ? 0 : 24 * u; //  0 →  0 → 24
  const a = lo ? c.a0 + (c.aSm - c.a0) * u : c.aSm + (c.aMd - c.aSm) * u;
  return `0 ${px(y)} ${px(b)} rgba(${c.shadowRgb}, ${a.toFixed(2)})`;
}

/** Build the card material tokens for a mode + the user's material prefs. */
export function resolveMaterial(mode: 'dark' | 'light', p: MaterialInput): MaterialTokens {
  const c = MATERIAL_CAPS[mode];
  const sheen = Math.min(1, Math.max(0, p.cardSheen));
  const grain = quantiseGrain(p.cardGrain);
  const t = Math.min(1, Math.max(0, p.cardElevation));
  // Edge light is GATED behind sheen: a specular edge on a matte card is the classic
  // fake-bevel tell, so it can never appear on its own.
  const spec = sheen * Math.min(1, Math.max(0, p.cardEdge));

  const img: string[] = [];
  const size: string[] = [];
  if (sheen > 0) {
    const a = (c.sheen * sheen).toFixed(4);
    // Dark: white key light from the top. Light: an INK grounding wash from the
    // bottom — a white highlight on a near-white card is physically invisible.
    img.push(
      mode === 'dark'
        ? `linear-gradient(180deg, rgba(${c.sheenRgb},${a}) 0, rgba(${c.sheenRgb},0) ${c.sheenBandPx}px)`
        : `linear-gradient(0deg, rgba(${c.sheenRgb},${a}) 0, rgba(${c.sheenRgb},0) ${c.sheenBandPx}px)`,
    );
    size.push('100% 100%');
  }
  if (grain > 0) {
    img.push(grainTile(grain, mode));
    size.push(`${TILE}px ${TILE}px`);
  }

  const edgeInset =
    spec > 0 ? `inset 0 1px 0 rgba(255, 255, 255, ${(c.edge * spec).toFixed(2)})` : '';

  return {
    sheen,
    grain,
    edge: Math.min(1, Math.max(0, p.cardEdge)),
    elevation: t,
    backgroundImage: img.length ? img.join(', ') : 'none',
    backgroundSize: size.length ? size.join(', ') : 'auto',
    backgroundRepeat: 'repeat',
    shadow: [elevationFor(t, mode), edgeInset].filter(Boolean).join(', '),
    shadowElevated: [elevationFor(Math.min(1, t + 0.5), mode), edgeInset || c.mdInset]
      .filter(Boolean)
      .join(', '),
  };
}
